import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, MessageSquare, QrCode, Zap, Users, Home, TrendingUp } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { BottomSheet } from "./BottomSheet";
import { api } from "../lib/api";

export function PromoInviteSheet({ open, onClose }) {
  const [promo, setPromo] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const { data } = await api.get("/promotions/active"); setPromo(data); }
      catch { setPromo(null); }
    })();
  }, [open]);

  const link = promo?.code ? `${window.location.origin}/r/${promo.code}` : "";
  const message = `Come join me on The Family Vault — one private home for our family to connect, plan, and preserve our legacy. 💫\n\nHere's your personal invite: ${link}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — link: " + link); }
  };

  const textMessage = () => { window.location.href = `sms:?&body=${encodeURIComponent(message)}`; };

  const Tile = ({ icon: Icon, label, onClick, testid, active }) => (
    <motion.button whileTap={{ scale: 0.95 }} onClick={onClick} data-testid={testid}
      className="flex flex-col items-center justify-center gap-2 rounded-2xl py-5 card-surface"
      style={active ? { border: "1px solid color-mix(in srgb, var(--accent) 50%, transparent)" } : {}}>
      <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
        <Icon size={20} className="gold-text" />
      </div>
      <span className="text-xs font-semibold">{label}</span>
    </motion.button>
  );

  const r = promo?.rewards || {};
  const Reward = ({ icon: Icon, credits, label }) => (
    <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: "var(--overlay)" }} data-testid="promo-reward-row">
      <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
        <Icon size={17} className="gold-text" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>+{credits} credits</div>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{label}</div>
      </div>
    </div>
  );

  return (
    <BottomSheet open={open} onClose={() => { setShowQR(false); onClose(); }} title="Invite & Earn">
      <div data-testid="promo-invite-sheet">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
            <Zap size={22} className="gold-text" />
          </div>
          <h2 className="font-serif-display text-2xl leading-tight" style={{ color: "var(--text-primary)" }}>
            Invite your friends and earn free AI credits!
          </h2>
        </div>
        <p className="text-sm mt-2 mb-4" style={{ color: "var(--text-secondary)" }}>
          Share your personal link. When a friend takes an action, credits land in your Vault automatically.
        </p>

        <div className="space-y-2.5">
          {r.join_family > 0 && <Reward icon={Users} credits={r.join_family} label="A friend joins your family" />}
          {r.create_family > 0 && <Reward icon={Home} credits={r.create_family} label="A friend starts their own family" />}
          {r.upgrade > 0 && <Reward icon={TrendingUp} credits={r.upgrade} label="A friend upgrades to a paid plan" />}
        </div>
        <p className="text-[11px] mt-2.5" style={{ color: "var(--text-muted)" }}>
          Earned credits expire {promo?.expiry_days || 30} days after they're issued.
        </p>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <Tile icon={copied ? Check : Copy} label="Copy link" onClick={copyLink} testid="promo-copy-link" />
          <Tile icon={MessageSquare} label="Text" onClick={textMessage} testid="promo-text-message" />
          <Tile icon={QrCode} label="QR code" onClick={() => setShowQR((s) => !s)} testid="promo-qr-toggle" active={showQR} />
        </div>

        {showQR && link && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="mt-5 flex flex-col items-center" data-testid="promo-qr-panel">
            <div className="rounded-2xl p-4" style={{ background: "#fff" }}>
              <QRCodeCanvas value={link} size={200} fgColor="#171826" bgColor="#ffffff" includeMargin={false} />
            </div>
            <p className="text-[11px] mt-3 text-center" style={{ color: "var(--text-muted)" }}>
              Have your friend scan this with their phone camera.
            </p>
          </motion.div>
        )}
      </div>
    </BottomSheet>
  );
}
